import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { ClientAppEvent, EVOTRUX_BORDER_GREY } from "constants/index";
import { useDisclosure, useRoleContext, useWriterComponentState } from "hooks";
import { formatCentsWithCurrency } from "utils";

import type { EditableLineItem } from "./TfcEditableTable";

interface TfcSubmitButtonProps {
  shipmentId: number;
  shipperCompanyName: string;
  lineItems: EditableLineItem[];
  note: string;
  bookedPriceCents: number | null;
  currency: string;
  disabled: boolean;
  onSuccess: () => void;
}

export function TfcSubmitButton(props: TfcSubmitButtonProps) {
  const {
    shipmentId,
    shipperCompanyName,
    lineItems,
    note,
    bookedPriceCents,
    currency,
    disabled,
    onSuccess,
  } = props;
  const { isOpen, onOpen, onClose } = useDisclosure(false);
  const { role } = useRoleContext();
  const posthog = usePostHog();

  const totalCents =
    (bookedPriceCents ?? 0) +
    lineItems.reduce((sum, item) => sum + item.line_total_cents, 0);
  const formattedTotal = formatCentsWithCurrency(totalCents, currency);

  const { loading, write, error } = useWriterComponentState({
    errorMessage: "Failed to submit freight cost. Please try again.",
    writeConfig: {
      method: "post",
      url: `/shipments/${shipmentId}/tfc`,
      body: {
        line_items: lineItems.map(({ description, line_total_cents }) => ({
          description,
          line_total_cents,
        })),
        notes: note || undefined,
      },
    },
  });

  const handleConfirm = () => {
    onClose();
    write(() => {
      posthog?.capture(ClientAppEvent.TFC_SUBMITTED, {
        shipment_id: shipmentId,
        shipper_company_name: shipperCompanyName,
        // TFC submit access is limited to the booked carrier's own company members,
        // so the submitter's role company is the booked carrier company.
        carrier_company_name: role?.company?.name ?? null,
      });
      onSuccess();
    });
  };

  const confirmMessage =
    lineItems.length === 0 ? (
      <>
        You&apos;re confirming the booked price of{" "}
        <strong>{formattedTotal}</strong> as the final freight cost. No
        additional charges have been added. Continue?
      </>
    ) : (
      <>
        You&apos;re submitting a total freight cost of{" "}
        <strong>{formattedTotal}</strong> (booked price plus {lineItems.length}{" "}
        additional charge
        {lineItems.length > 1 ? "s" : ""}
        ). The shipper will be notified for review. Continue?
      </>
    );

  return (
    <>
      <Button
        variant="contained"
        disabled={disabled || loading}
        onClick={onOpen}
        sx={{
          px: 3,
          "&.Mui-disabled": {
            backgroundColor: EVOTRUX_BORDER_GREY,
            color: "#9CA3AF",
          },
        }}
      >
        SUBMIT TO SHIPPER
      </Button>

      <ErrorTypography error={error} />

      <Dialog
        open={isOpen}
        onClose={onClose}
        PaperProps={{ sx: { borderRadius: "8px", p: 1, maxWidth: "440px" } }}
      >
        <DialogTitle sx={{ fontSize: "17px", fontWeight: 500, pb: 1 }}>
          {lineItems.length === 0
            ? "Confirm Freight Cost"
            : "Submit Freight Cost"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            component="div"
            sx={{ fontSize: "14px", color: "#4B5563" }}
          >
            {confirmMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={onClose} sx={{ px: 3 }}>
            CANCEL
          </Button>
          <Button variant="contained" onClick={handleConfirm} sx={{ px: 3 }}>
            CONFIRM
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
